import configLib from '../../../configLib';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { CharacterEnum } from '../../enums/character.enum';
import { GWQueryCase, QueryCacheItemType } from '../../models/gateway/types';
import { dataQuery } from '../../store/gateway';
import { REST_RETRIES_QUICK_DELAY } from '../../store/gateway/constants';
import { uuidv4 } from '../../util/tools';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { BaseSubscriptionRequestType } from '../subscription/types';
import { GatewayWebSocketService } from '../web-socket/GatewayWebSocket.service';
import { GatewayRequestType, GatewayResponseType } from '../web-socket/types';

import { MarketDataQueryData, MarketDataQueryRequest } from './types';

const LOG_PREFIX = '[MDQ-WS]';
const logger = LoggerService.getLogger();

const isMarketDataQueryRequest = (
  request: BaseSubscriptionRequestType,
): request is MarketDataQueryRequest => request.messageType === GatewayRequestType.MarketDataQuery;

export class MarketDataQueryService {
  protected readonly gatewayService: GatewayWebSocketService;

  private static instance: MarketDataQueryService | undefined;

  private readonly requestTimestamps = new Map<string, number>();

  protected constructor() {
    this.gatewayService = GatewayWebSocketService.getInstance();
    this.registerResponseHandler();
  }

  public static getInstance(): MarketDataQueryService {
    this.instance ??= new MarketDataQueryService();
    return this.instance;
  }

  protected registerResponseHandler(): void {
    const handler = (event: WebSocketEventData<any>) => this.handleResponseEvent(event);

    this.gatewayService.subscribeToResponses(GatewayResponseType.DataQueryResponse, handler);
    this.gatewayService.subscribeToResponses(GatewayResponseType.MarketDataQuery, handler);
  }

  public handleResponseEvent = (event: WebSocketEventData<any>): void => {
    const data = event.data ?? {};
    const { accessId, traceIdentifier } = data;

    const traceIdentifierRequest = this.gatewayService
      .getRequestedObjectList()
      .find(item => item.traceIdentifier === traceIdentifier) ?? {} as MarketDataQueryRequest;

    if (!traceIdentifier || !traceIdentifierRequest || !isMarketDataQueryRequest(traceIdentifierRequest)) {
      logger.warn(`${LOG_PREFIX} Response ignored due to missing cache entry`, { traceIdentifier, accessId });
      return;
    }

    if (!accessId) {
      logger.warn(`${LOG_PREFIX} Response ignored due to missing accessId`, { traceIdentifier, messageType: data.messageType });
      return;
    }

    const { symbols, bucket, stockExchangeCode, endTime, startTime } = traceIdentifierRequest;

    configLib.store.dispatch(
      dataQuery({
        accessId,
        traceIdentifier,
        estimatedDeliveryInSec: REST_RETRIES_QUICK_DELAY,
        cacheData: {
          queryCase: GWQueryCase.OHLCV,
          symbol: symbols?.[0] ?? null,
          requestData: { symbols, bucket, stockExchangeCode, endTime, startTime },
        } as QueryCacheItemType,
      }),
    );
  };

  private createRequestKey(data: MarketDataQueryData): string {
    const symbolKey = data.symbols.join(CharacterEnum.Hyphen);

    return [
      data.bucket,
      data.stockExchangeCode || CharacterEnum.Empty,
      data.startTime || CharacterEnum.Empty,
      data.endTime || CharacterEnum.Empty,
      symbolKey,
    ].join(CharacterEnum.Pipe);
  }

  private shouldSendRequest(data: MarketDataQueryData): boolean {
    const requestKey = this.createRequestKey(data);
    const now = Date.now();
    const lastSentAt = this.requestTimestamps.get(requestKey);

    if (lastSentAt != null && now - lastSentAt < PredefinedMilisecondsTypeEnum.oneSecond) {
      return false;
    }

    this.requestTimestamps.set(requestKey, now);
    return true;
  }

  public sendMarketDataQuery(data: MarketDataQueryData, isOnline = true): void {
    if (!this.gatewayService.isConnected() || !isOnline) {
      logger.warn(`${LOG_PREFIX} MarketDataQuery failed! isOnline:${isOnline}`);
      return;
    }

    if (!this.shouldSendRequest(data)) {
      return;
    }

    this.gatewayService.sendRequest({
      ...data,
      messageType: GatewayRequestType.MarketDataQuery,
      traceIdentifier: uuidv4(),
    } as MarketDataQueryRequest);
  }
}
